import { Can } from "@/casl.config";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import { isLine } from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/types";
import { lineSchemaToMutationLine } from "@components/Dashboard/pages/Sales/Orders/CreationPage";
import { toastError } from "@helpers/utils";
import {
    useConfirmSaleOrderMutation,
    useGetSaleOrderQuery,
    useUpdateSaleOrderMutation,
} from "@redux/features/sales/orders/salesOrdersApi";
import BaseRecordRenderer from "@reusables/BaseRecordRenderer";
import { BaseLoadingBlocker } from "@reusables/blockers/BaseLoadingBlocker";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import IntegrationsBadge from "../components/IntegrationsBadge";
import MutationLayout from "../components/MutationLayout";
import { BadgeStatus } from "@/types/general";
import { buildSaleOrderTheirReferencePayload } from "@components/Dashboard/pages/Sales/Orders/utils";

export default function SaleOrderEditingPage() {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.mutations.editing" });

    const history = useHistory();
    const { id } = useParams<{ id: string }>();

    const [updateSaleOrder, { isLoading: isUpdateLoading }] = useUpdateSaleOrderMutation();
    const [confirmSaleOrder, { isLoading: isConfirmOfferLoading }] = useConfirmSaleOrderMutation();
    const { data: saleOrder, isLoading: isSaleOrderLoading } = useGetSaleOrderQuery(parseInt(id));

    return (
        <BaseRecordRenderer
            record={saleOrder?.is_editable}
            loading={isSaleOrderLoading}
            notFoundText={t("notFoundText")}
        >
            <Can not I="edit" a="sale_order">
                <NoPermissionBanner />
            </Can>

            <Can I="edit" a="sale_order">
                <>
                    <BaseLoadingBlocker active={isUpdateLoading || isConfirmOfferLoading}>
                        <div className="space-y-[40px]">
                            <div className="flex flex-wrap items-center justify-between gap-[16px]">
                                <div className="flex min-w-0 flex-wrap items-center gap-[12px]">
                                    <h6 className="mb-0 text-accent">
                                        {t("heading")} #{saleOrder?.code ?? saleOrder?.id}
                                    </h6>
                                    <div id={"sale_order_status_header"} className="shrink-0"></div>
                                </div>

                                <div className="ml-auto flex flex-wrap items-center gap-[16px]">
                                    <div id={"reference_number"} className="min-w-[220px]"></div>
                                    <IntegrationsBadge row={saleOrder} width={24} height={24} />
                                </div>
                            </div>
                            <MutationLayout
                                saleOrder={saleOrder}
                                onSubmit={(data) => {
                                    const mutationData = {
                                        customer: data.customer.id,
                                        order_date: data.order_date.format("YYYY-MM-DD"),
                                        preferred_delivery_date:
                                            data.preferred_delivery_date?.format("YYYY-MM-DD"),
                                        reference_number: data.reference_number.code,
                                        our_reference: data.our_reference?.id,
                                        ...buildSaleOrderTheirReferencePayload(data),
                                        payment_terms: data.payment_terms?.id,
                                        delivery_terms: data.delivery_terms?.id,
                                        comment: data.comment,
                                        language: data.language?.id,

                                        ...(!data.is_billing_for_delivery &&
                                            !!data.delivery_address && {
                                                delivery_address: data.delivery_address.id,
                                            }),

                                        is_billing_for_delivery: data.is_billing_for_delivery,

                                        reservations: data.reservations?.map((res) => ({
                                            product_id: res.product.id,
                                            quantity: res.quantity,
                                        })),
                                        reservations_expiration_date:
                                            data.reservations_expiration_date?.format("YYYY-MM-DD"),

                                        ...(!!data.twenty_four_seven_office?.project && {
                                            project: data.twenty_four_seven_office.project,
                                        }),

                                        lines: data.lines.map((line) => {
                                            if (isLine(line)) {
                                                return lineSchemaToMutationLine(line);
                                            } else {
                                                return {
                                                    type: "group" as const,
                                                    data: {
                                                        id: line.id,
                                                        name: line.name,
                                                        module: line.module,
                                                        module_entity_id: line.module_entity_id,
                                                        comment: line.comment,
                                                        lines: line.lines.map(lineSchemaToMutationLine),
                                                    },
                                                };
                                            }
                                        }),

                                        deleted_lines: data.deleted_lines,
                                    };

                                    const shouldConfirmOffer =
                                        saleOrder?.status.name === BadgeStatus.Base.offer &&
                                        data.status === BadgeStatus.Base.new;

                                    const runMutation = async () => {
                                        await updateSaleOrder({
                                            id: saleOrder?.id as number,
                                            ...mutationData,
                                        }).unwrap();

                                        if (shouldConfirmOffer) {
                                            await confirmSaleOrder({
                                                id: saleOrder?.id as number,
                                            }).unwrap();
                                        }

                                        toast.success(t("responses.success"));
                                        history.push(
                                            `/dashboard/sales/orders/${saleOrder?.id}/details`
                                        );
                                    };

                                    void runMutation().catch((e) => {
                                        toastError(e);
                                    });
                                }}
                            />
                        </div>
                    </BaseLoadingBlocker>
                    <div className="h-[220px]" aria-hidden="true" />
                </>
            </Can>
        </BaseRecordRenderer>
    );
}
